import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationService } from '../../../../../services/navigation.service';
import { FormService } from '../../../../../services/form.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormGroup } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SubstanceTypeEnum } from 'app/enums/substance-type-enum';

@Component({
  selector: 'app-suggested-names',
  templateUrl: './suggested-names.component.html',
  styleUrls: ['./suggested-names.component.scss']
})
export class SuggestedNamesComponent implements OnInit, OnDestroy {
  @Input({required: true}) substanceType!: SubstanceTypeEnum;
  @ViewChild('suggestedNamesInput') suggestedNamesInput!: ElementRef;
  nameToAdd!: string;
  fieldUpdateValueMap: Map<number, string> = new Map();
  private sizeChangeSubscription!: Subscription;
  private formSubscription!: Subscription;
  private mobileDeviceSize: boolean = false;
  preventBlur: boolean = false;
  suggestedSingleName!: string;
  formType!: string;
  maxSuggestedNames: number = 10;

  constructor(
    protected navigationService: NavigationService, 
    protected formService: FormService, 
    private responsive: BreakpointObserver) {}

  ngOnInit(): void {
    this.sizeChangeSubscription = this.responsive.observe([Breakpoints.Handset]).subscribe(result => {
      if (result.matches) {
        this.mobileDeviceSize = true;
      } else {
        this.mobileDeviceSize = false;
      }
    });
     this.formSubscription = this.formService.getCurrentUsanFormUpdate().subscribe(
      usanForm => {
        let form = usanForm.value;
        this.populateFieldUpdateValueMap(form);
      }
    );
    this.formType = this.currentForm.get('formType')?.value;
    if(this.formType === 'FormC' && this.suggestedNames) { //For Form C, which has slightly diff logic than rest of forms
      this.suggestedSingleName = this.suggestedNames[0];
    }
    if(this.formType === 'FormE') {
      this.maxSuggestedNames = 5;
    }
  }

  ngOnDestroy(): void {
    this.sizeChangeSubscription.unsubscribe();
    this.formSubscription.unsubscribe();
  }

  private populateFieldUpdateValueMap(form: FormGroup) {
    this.fieldUpdateValueMap = new Map<number, string>();
    const values = form.get('suggestedNamesFormA')?.value;
    if(values) {
      values.forEach((value, index) => {
        this.fieldUpdateValueMap.set(index, value);
      });

    }
  }

  drop(event: CdkDragDrop<string[]>) {
    let suggestedNames: string[] = this.currentForm.get('suggestedNamesFormA')?.value;
    moveItemInArray(suggestedNames, event.previousIndex, event.currentIndex);
    this.formService.updateUsanFormValue('suggestedNamesFormA', suggestedNames);
  }

  public addSuggestedName(event?: FocusEvent) {
    if(this.preventBlur) {
      this.preventBlur = false;
      return;
    }
    if(this.formType === 'FormC') 
        this.nameToAdd = this.suggestedSingleName;
    
    if (this.nameToAdd) {
      let suggestedNames = this.currentForm.get('suggestedNamesFormA')?.value;
      if(!suggestedNames || this.formType === 'FormC') //Form C can have only one sugg name
        suggestedNames = [];
      suggestedNames.push(this.nameToAdd);
      this.formService.updateUsanFormValue('suggestedNamesFormA', suggestedNames);
      this.nameToAdd = '';
    }
    this.errorCheck();
  }

  public removeSuggestedName() {
    this.preventBlur = true;
    if(this.nameToAdd) {
      this.nameToAdd = '';
    }
    this.errorCheck();
  }

  public updateSuggestedName(fieldId: number, event?: any) {
     let map: Map<number, string> = this.fieldUpdateValueMapping;
    if(event?.target?.value) {
      map.set(fieldId, event?.target?.value);
    } else {
      map.delete(fieldId);
    }
    let names: string[] = Array.from(map.values());
    this.formService.updateUsanFormValue('suggestedNamesFormA', names);
    this.errorCheck();
  }

  public cancelEdit(fieldId: number) {
    let map: Map<number, string> = this.fieldUpdateValueMapping;
    if (map.get(fieldId)) {
      // Ensure the onBlur event doesn't overwrite the value!
      let names: string[] = Array.from(map.values());
      this.formService.updateUsanFormValue('suggestedNamesFormA', names);
    }
    this.errorCheck();
  }

  public clearSuggestedNameOnEdit(fieldId: number) {
    this.updateSuggestedName(fieldId);
    this.errorCheck();
  }

  public submitAnotherName() {
    this.addSuggestedName();

    for (let i = 0; i < this.maxSuggestedNames; i++) {
        this.updateSuggestedName(i);
    }
  }

  public canAddSuggestedName() {
    return this.currentForm.get('suggestedNamesFormA')?.value.length < this.maxSuggestedNames;
  }

  private errorCheck() {
    const targetControl = this.currentForm.get('suggestedNamesFormA');
    
    if(!targetControl) {
      return;
    }

    // Force re-validation
    setTimeout(() => {
      targetControl.markAsTouched();
      targetControl.markAsDirty();
      targetControl.updateValueAndValidity();
    });
  }


  public hasFieldValidationError(fieldName: string, errorName?: string): boolean {
    return this.formService.hasFieldValidationError(fieldName, errorName);
  }

  public getSuggestedName(fieldId: number): string {
    return this.currentForm.get('suggestedNamesFormA')?.value[fieldId];
  }

  get suggestedNames(): string[] {
    return this.currentForm.get('suggestedNamesFormA')?.value;
  }

  get multipleSuggestedNames(): boolean {
    return this.currentForm?.get('suggestedNamesFormA')?.value.length > 0;
  }
  
  get isMobileDeviceSize() : boolean {
    return this.mobileDeviceSize;
  }

  get isNoneSubstanceType() {
    return this.substanceType === SubstanceTypeEnum.None;
  }

  get isSingleEntitySubstanceType() {
    return this.substanceType === SubstanceTypeEnum.SingleEntity;
  }

  get isSaltSubstanceType() {
    return this.substanceType === SubstanceTypeEnum.Salt;
  }

  get isSingleEntityAndSaltSubstanceType() {
    return this.substanceType === SubstanceTypeEnum.SingleEntityAndSalt;
  }

  get isRevisionSubstanceType() { 
    return this.substanceType === SubstanceTypeEnum.Revision;
  }

  get formFieldTitle() {
    var title = `Suggested name(s)`;
    
    if(this.isSingleEntitySubstanceType) {
      title += ' for single entity'; 
    }
    
    if(this.formType === 'FormC') {
      title = 'Suggested name for salt form'; //Form C can only have 1 sugg name
    }

    if(this.isSingleEntityAndSaltSubstanceType) {
      title += ' for single entity drug';
    }

    if(this.isRevisionSubstanceType) {
      return ` in order of preference`;
    }
    
    return title;
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  get fieldUpdateValueMapping(): Map<number, string> {
    let mapping = new Map<number, string>();
    let values = this.currentForm.get('suggestedNamesFormA')?.value;
    if(values) {
      values.forEach((value, index) => {
        mapping.set(index, value);
      })

    }
    return mapping;
  }


}
